import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@structural-climbing/auth'
import type { DocumentStatement } from './DocumentSignPanel'

/**
 * Fired on `window` when something the member did may have created a new
 * document to sign — adding a child, today. The gate lives in the root layout,
 * far from the screen that caused it, so it listens rather than being told.
 */
export const PENDING_DOCUMENTS_CHANGED = 'sc:pending-documents-changed'

export interface PendingChild {
  id: string
  first_name: string
  last_name: string | null
}

export interface PendingDocument {
  id: string
  title: string
  r2_key: string
  version: number
  sort_order: number
  /** Set when the document is signed on this child's behalf; null for the member's own. */
  child: PendingChild | null
  /**
   * Set when the member already signed this version and is asked again: their
   * own name changed, or the child's did. The certificate prints both.
   */
  reason?: 'name_changed' | 'child_name_changed' | 'came_of_age' | null
  /**
   * Set when the member is under 18 and a parent or guardian signs this one
   * for them. The gate asks for the guardian before showing it.
   */
  guardian_required?: boolean
  /** Ticked one by one before signing. Absent from a Worker older than this app. */
  statements?: DocumentStatement[]
}

/** The gym's privacy notice, linked from the signing screen. */
export interface PrivacyNotice {
  id: string
  version: number
  /** Relative to the API: a public URL the signer can open in a new tab. */
  path: string
}

/** Whether this gym wants a proved address before signing, and where the member stands. */
export interface EmailVerificationState {
  required: boolean
  verified: boolean
  email: string | null
}

/** The parent or guardian who signed for this member last time. */
export interface PendingGuardian {
  first_name: string | null
  last_name: string | null
  email: string | null
  /** Whether the guardian's address must be proved by a code before signing. */
  email_required: boolean
  /** The member's own address, which the guardian's must differ from. */
  member_email: string | null
  /** The member a guardian signs for, named in the declaration. */
  member_name: string | null
  /** The gym the code is sent for. */
  gym_slug: string | null
  gym_name: string | null
}

interface PendingResponse {
  documents: PendingDocument[]
  guardian?: PendingGuardian | null
  privacy_notice?: PrivacyNotice | null
  email_verification?: EmailVerificationState | null
}

interface UsePendingDocumentsResult {
  pendingDocs: PendingDocument[]
  privacyNotice: PrivacyNotice | null
  /** Null from a Worker older than this app: then nothing is asked. */
  emailVerification: EmailVerificationState | null
  /** Set when a parent or guardian has to sign what is pending. */
  guardian: PendingGuardian | null
  loading: boolean
  refetch: () => void
}

/**
 * @param gymSlug the gym the member is currently looking at. The API scopes
 * pending documents to it; without it the server falls back to the single gym
 * in the JWT, which for a multi-gym member is not the one on screen.
 */
export function usePendingDocuments(gymSlug?: string | null): UsePendingDocumentsResult {
  const { isAuthenticated, getAccessTokenSilently } = useAuth()
  const [pendingDocs, setPendingDocs] = useState<PendingDocument[]>([])
  const [privacyNotice, setPrivacyNotice] = useState<PrivacyNotice | null>(null)
  const [emailVerification, setEmailVerification] = useState<EmailVerificationState | null>(null)
  const [guardian, setGuardian] = useState<PendingGuardian | null>(null)
  const [loading, setLoading] = useState(false)

  const query = gymSlug ? `?gym=${encodeURIComponent(gymSlug)}` : ''

  useEffect(() => {
    if (!isAuthenticated) return
    let cancelled = false
    setLoading(true)

    async function doFetch() {
      try {
        const token = await getAccessTokenSilently()
        if (cancelled) return
        const res = await window.fetch(
          `${import.meta.env.VITE_API_URL as string}/my-pending-documents${query}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (cancelled || !res.ok) return
        const data = (await res.json()) as PendingResponse
        if (cancelled) return
        setPendingDocs(data.documents)
        setPrivacyNotice(data.privacy_notice ?? null)
        setEmailVerification(data.email_verification ?? null)
        setGuardian(data.guardian ?? null)
      } catch {
        // silently fail — the gate simply won't appear
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void doFetch()
    return () => {
      cancelled = true
    }
    // getAccessTokenSilently is stable per Auth0 SDK contract — intentionally omitted from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, query])

  const refetch = useCallback(() => {
    if (!isAuthenticated) return
    setLoading(true)
    void getAccessTokenSilently()
      .then((token) =>
        window.fetch(`${import.meta.env.VITE_API_URL as string}/my-pending-documents${query}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      )
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return
        setPendingDocs((data as PendingResponse).documents)
        setPrivacyNotice((data as PendingResponse).privacy_notice ?? null)
        setEmailVerification((data as PendingResponse).email_verification ?? null)
        setGuardian((data as PendingResponse).guardian ?? null)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [isAuthenticated, getAccessTokenSilently, query])

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible' && isAuthenticated) refetch()
    }
    function onChanged() {
      if (isAuthenticated) refetch()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener(PENDING_DOCUMENTS_CHANGED, onChanged)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener(PENDING_DOCUMENTS_CHANGED, onChanged)
    }
  }, [isAuthenticated, refetch])

  return { pendingDocs, privacyNotice, emailVerification, guardian, loading, refetch }
}
